import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeaderFour from "../../components/Header/HeaderFour";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import Seo from "../../components/Seo";
import makeRequest from "../../makeRequest";
import config from "../../config";

const STYLE_ID = "e-services-styles";

const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .es-section {
      padding: 80px 0 60px;
      background: #f8f9fa;
    }
    .es-section-header {
      text-align: center;
      margin-bottom: 50px;
    }
    .es-section-header h2 {
      font-size: 2.2rem;
      font-weight: 700;
      color: #1a2e4a;
      margin-bottom: 12px;
    }
    .es-section-header p {
      font-size: 1.05rem;
      color: #6c757d;
      max-width: 600px;
      margin: 0 auto;
    }
    .es-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 28px;
    }
    .es-card {
      background: #fff;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.07);
      transition: transform 0.25s ease, box-shadow 0.25s ease;
      display: flex;
      flex-direction: column;
    }
    .es-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(0,0,0,0.13);
    }
    .es-card-img {
      width: 100%;
      height: 200px;
      object-fit: cover;
    }
    .es-card-img-placeholder {
      width: 100%;
      height: 200px;
      background: linear-gradient(135deg, #e3f0ff 0%, #c9e2ff 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2.5rem;
      color: #4a90d9;
    }
    .es-card-body {
      padding: 22px 22px 18px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .es-card-title {
      font-size: 1.15rem;
      font-weight: 700;
      color: #1a2e4a;
      margin-bottom: 8px;
      line-height: 1.4;
    }
    .es-card-desc {
      font-size: 0.92rem;
      color: #6c757d;
      line-height: 1.6;
      flex: 1;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-bottom: 18px;
    }
    .es-card-btn {
      display: inline-block;
      padding: 10px 22px;
      background: #1a6fc4;
      color: #fff !important;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 600;
      text-decoration: none !important;
      align-self: flex-start;
      transition: background 0.2s;
    }
    .es-card-btn:hover {
      background: #145ea3;
      color: #fff !important;
    }
    .es-loading {
      text-align: center;
      padding: 80px 20px;
      color: #6c757d;
      font-size: 1.1rem;
    }
    .es-empty {
      text-align: center;
      padding: 80px 20px;
      color: #adb5bd;
      font-size: 1rem;
    }
    @media (max-width: 576px) {
      .es-grid {
        grid-template-columns: 1fr;
      }
    }
  `;
  document.head.appendChild(style);
};

function EServiceCard({ item }) {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!item.hasImage) return;
    let objectUrl = null;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${config.api.baseUrl}/website/e-service/${item.id}/image`);
        if (!res.ok) return;
        const blob = await res.blob();
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImgSrc(objectUrl);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id, item.hasImage]);

  const detailUrl = `/e-services/${item.id}`;
  const btnLabel = item.buttonName || "Learn More";
  const btnHref = item.buttonLink || detailUrl;
  const isExternal = item.buttonLink && (item.buttonLink.startsWith("http://") || item.buttonLink.startsWith("https://"));

  return (
    <div className="es-card">
      {imgSrc ? (
        <img src={imgSrc} alt={item.title || item.name} className="es-card-img" />
      ) : (
        <div className="es-card-img-placeholder">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
        </div>
      )}
      <div className="es-card-body">
        <div className="es-card-title">{item.title || item.name}</div>
        {item.description && <div className="es-card-desc">{item.description}</div>}
        {isExternal ? (
          <a href={btnHref} target="_blank" rel="noopener noreferrer" className="es-card-btn">
            {btnLabel}
          </a>
        ) : (
          <Link to={detailUrl} className="es-card-btn">
            {btnLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

function EServicesMain() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    injectStyles();
    (async () => {
      try {
        const res = await makeRequest({ route: "/website/e-service?noPagination=1", method: "get" });
        setItems(res?.data || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="es-section">
      <div className="container">
        <div className="es-section-header">
          <h2>E-Services</h2>
          <p>Access our digital healthcare services conveniently from anywhere.</p>
        </div>
        {loading ? (
          <div className="es-loading">Loading e-services…</div>
        ) : items.length === 0 ? (
          <div className="es-empty">No e-services available at this time.</div>
        ) : (
          <div className="es-grid">
            {items.map((item) => (
              <EServiceCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const EServices = () => (
  <>
    <Seo title="E-Services | Maroof International Hospital" />
    <HeaderFour />
    <Breadcrumb title="E-Services" />
    <EServicesMain />
    <Footer />
  </>
);

export default EServices;
